import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import type { Tag } from "../types/orderbook";
import { logger } from "../lib/logger";

// Fetch all tags for the current user
async function fetchTags(): Promise<Tag[]> {
  const {
    data: { user }
  } = await supabase.auth.getUser();
  
  if (!user) {
    return [];
  }

  const { data, error } = await supabase
    .from("tags")
    .select("*")
    .eq("user_id", user.id)
    .order("name", { ascending: true });

  if (error) {
    logger.error("Error loading tags", error);
    throw error;
  }

  return (data ?? []) as Tag[];
}

// Hook to get all tags
export function useTags() {
  return useQuery({
    queryKey: ["tags"],
    queryFn: fetchTags,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
}

// Generate a random very light color from a predefined palette
function getRandomColor(): string {
  const colors = [
    // Reds & Pinks
    "#FEF2F2", // very light red/pink
    "#FDF2F8", // very light pink
    "#FCE7F3", // very light rose
    "#FFF1F2", // very light rose-red
    "#FEE2E2", // light red
    "#FECACA", // light rose
    "#FBCFE8", // light pink
    
    // Oranges & Yellows
    "#FFF7ED", // very light orange
    "#FFEDD5", // light orange
    "#FED7AA", // very light peach
    "#FFE4B5", // light peach
    "#FFFBEB", // very light amber/yellow
    "#FEF3C7", // very light yellow
    "#FEF9C3", // light yellow
    "#FDE68A", // light amber
    
    // Greens & Limes
    "#F7FEE7", // very light lime/green
    "#ECFDF5", // very light green
    "#D1FAE5", // very light emerald
    "#A7F3D0", // light green
    "#D9F99D", // light lime
    "#E6FFFA", // very light mint
    
    // Blues & Cyans
    "#ECFEFF", // very light cyan
    "#CFFAFE", // light cyan
    "#E0F2FE", // very light sky blue
    "#BAE6FD", // light sky blue
    "#EFF6FF", // very light blue
    "#DBEAFE", // light blue
    "#BFDBFE", // light blue-2
    
    // Purples & Violets
    "#E0E7FF", // very light indigo
    "#C7D2FE", // light indigo
    "#F5F3FF", // very light purple
    "#EDE9FE", // light purple
    "#F3E8FF", // very light violet
    "#E9D5FF", // light violet
    "#FAE8FF", // very light fuchsia
    "#F5D0FE", // light fuchsia
    
    // Teals & Turquoises
    "#F0FDFA", // very light teal
    "#CCFBF1", // light teal
    "#E6FFFA", // very light turquoise
    
    // Grays & Neutrals
    "#F9FAFB", // very light gray
    "#F3F4F6", // light gray
    "#F4F3FF", // very light lavender-gray
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

// Convert hex to RGB
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

// Get darker version of a color for text
function getDarkerColor(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#1F2937"; // fallback dark gray
  
  // Darken by reducing each RGB value by 40%
  const r = Math.max(0, Math.floor(rgb.r * 0.4));
  const g = Math.max(0, Math.floor(rgb.g * 0.4));
  const b = Math.max(0, Math.floor(rgb.b * 0.4));
  
  return `#${[r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('')}`;
}

// Hook to create a tag
export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      name: string;
      color?: string | null;
    }) => {
      const {
        data: { user },
        error: userError
      } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error("Not authenticated");
      }

      // Check if tag with same name already exists (case-insensitive)
      const { data: existingTags } = await supabase
        .from("tags")
        .select("id, name")
        .eq("user_id", user.id)
        .ilike("name", payload.name.trim());

      if (existingTags && existingTags.length > 0) {
        throw new Error(`Tag "${payload.name.trim()}" already exists`);
      }

      const { data, error } = await supabase
        .from("tags")
        .insert({
          name: payload.name.trim(),
          color: payload.color ?? getRandomColor(),
          user_id: user.id
        })
        .select("*")
        .single();

      if (error) {
        logger.error("Error creating tag", error);
        throw error;
      }

      logger.log("Tag created successfully:", data.id);
      return data as Tag;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
    }
  });
}

// Hook to update a tag
export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      tagId: string;
      updates: {
        name?: string;
        color?: string | null;
      };
    }) => {
      const { data, error } = await supabase
        .from("tags")
        .update(params.updates)
        .eq("id", params.tagId)
        .select("*")
        .single();

      if (error) {
        logger.error("Error updating tag", error);
        throw error;
      }

      return data as Tag;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      queryClient.invalidateQueries({ queryKey: ["itemTags"] });
    }
  });
}

// Hook to delete a tag
export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tagId: string) => {
      // First, delete all item_tags relationships
      const { error: itemTagsError } = await supabase
        .from("item_tags")
        .delete()
        .eq("tag_id", tagId);

      if (itemTagsError) {
        logger.error("Error deleting item_tags", itemTagsError);
        throw itemTagsError;
      }

      // Then delete the tag
      const { error } = await supabase
        .from("tags")
        .delete()
        .eq("id", tagId);

      if (error) {
        logger.error("Error deleting tag", error);
        throw error;
      }

      logger.log("Tag deleted successfully:", tagId);
      return tagId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      queryClient.invalidateQueries({ queryKey: ["itemTags"] });
      queryClient.invalidateQueries({ queryKey: ["items"] });
      queryClient.invalidateQueries({ queryKey: ["tagCounts"] });
    }
  });
}

// Fetch tag counts (number of items per tag)
async function fetchTagCounts(): Promise<Record<string, number>> {
  const {
    data: { user }
  } = await supabase.auth.getUser();
  
  if (!user) {
    return {};
  }

  // First get all user tags
  const { data: userTags, error: tagsError } = await supabase
    .from("tags")
    .select("id")
    .eq("user_id", user.id);

  if (tagsError) {
    logger.error("Error loading user tags for counts", tagsError);
    throw tagsError;
  }

  if (!userTags || userTags.length === 0) {
    return {};
  }

  const tagIds = userTags.map(t => t.id);

  // Then get all item_tags for those tags
  const { data, error } = await supabase
    .from("item_tags")
    .select("tag_id")
    .in("tag_id", tagIds);

  if (error) {
    logger.error("Error loading tag counts", error);
    throw error;
  }

  // Count items per tag
  const counts: Record<string, number> = {};
  (data ?? []).forEach((itemTag: { tag_id: string }) => {
    counts[itemTag.tag_id] = (counts[itemTag.tag_id] || 0) + 1;
  });

  return counts;
}

// Hook to get tag counts
export function useTagCounts() {
  return useQuery({
    queryKey: ["tagCounts"],
    queryFn: fetchTagCounts,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
}
